import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiLock, FiEye, FiEyeOff, FiCheckCircle, FiAlertCircle, FiArrowLeft } from 'react-icons/fi';
import apiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import logger from '../../utils/logger';
import { AuthPage, AuthCard } from './AuthLayout';

const Container = styled.div`
  width: 100%;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const Title = styled.h2`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.125rem;
  }
`;

const Subtitle = styled.p`
  color: var(--text-muted);
  font-size: 0.875rem;
  margin: 0;
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  position: relative;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  height: 48px;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid var(--border-color);
  font-family: 'Inter', sans-serif;
  border-radius: var(--control-radius, var(--card-radius));
  font-size: 0.95rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  background-color: var(--background-primary);
  color: var(--text-primary);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }
  
  &::placeholder {
    color: var(--text-muted);
  }
  
  &:disabled {
    background-color: var(--background-secondary);
    color: var(--text-muted);
    cursor: not-allowed;
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  left: 0.75rem;
  color: var(--text-muted);
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ToggleButton = styled.button`
  position: absolute;
  right: 0.75rem;
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.5rem;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
  
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: var(--text-primary);
    }
  }
`;

const Button = styled.button`
  width: 100%;
  height: 48px;
  padding: 0.75rem 1.5rem;
  background-color: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: var(--control-radius, var(--card-radius));
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  @media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled) {
      filter: brightness(0.95);
      transform: translateY(-1px);
      box-shadow: var(--shadow-md);
    }
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  font-size: 0.75rem;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PasswordRequirements = styled.div`
  background: var(--background-tertiary, var(--background-secondary));
  border: 1px solid var(--border-color);
  border-radius: var(--card-radius);
  padding: 0.75rem;
  margin-top: 0.5rem;
`;

const Requirement = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-bottom: 0.25rem;

  &:last-child {
    margin-bottom: 0;
  }

  &.valid {
    color: var(--success-color);
  }

  &.invalid {
    color: var(--error-color);
  }

  &.info {
    color: var(--text-muted);
    font-size: 0.8rem;
  }
`;

const RequirementIcon = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.valid ? 'var(--success-color)' : 'var(--error-color)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-inverse);
  font-size: 8px;
  font-weight: bold;
  
  ${props => props.children === '•' && `
    background: var(--text-muted);
    color: var(--background-primary);
    font-size: 10px;
    font-weight: bold;
  `}
`;

const SuccessMessage = styled(motion.div)`
  background: rgba(56, 161, 105, 0.08);
  border: 1px solid rgba(56, 161, 105, 0.25);
  border-radius: var(--card-radius);
  color: rgba(56, 161, 105, 0.95);
  font-size: 0.875rem;
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const BackLink = styled.button`
  background: none;
  border: none;
  color: var(--primary-color);
  cursor: pointer;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  transition: color 0.2s ease;
  padding: 0.25rem 0;
  
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: var(--primary-dark);
    }
  }
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [token, setToken] = useState('');

  const validatePassword = (password) => {
    const requirements = {
      length: password.length >= 10,
      digit: /\d/.test(password),
      specialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
    };
    return requirements;
  };

  const passwordRequirements = validatePassword(formData.password);
  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);

  useEffect(() => {
    // Force logout if user is logged in when accessing password reset
    if (user) {
      logger.debug('User is logged in, forcing logout before password reset');
      logout();
    }
  }, [user, logout]);

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setError('Invalid or missing reset token');
      return;
    }
    setToken(tokenParam);
  }, [searchParams]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const validateForm = () => {
    if (!formData.password) {
      setError('Password is required');
      return false;
    }

    if (formData.password.length < 10) {
      setError('Password must be at least 10 characters long');
      return false;
    }

    // Check if password contains at least one digit
    if (!/\d/.test(formData.password)) {
      setError('Password must contain at least one digit');
      return false;
    }

    // Check if password contains at least one special character
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(formData.password)) {
      setError('Password must contain at least one special character');
      return false;
    }

    if (!formData.confirmPassword) {
      setError('Please confirm your password');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      await apiService.resetPassword(token, formData.password);
      setSuccess(true);
      
      // Get preserved redirect from localStorage
      const preservedRedirect = localStorage.getItem('faithinsight_login_redirect');
      const loginUrl = preservedRedirect 
        ? `/login?redirect=${encodeURIComponent(preservedRedirect)}`
        : '/login';
      
      // Redirect to login after 3 seconds (with preserved redirect if available)
      setTimeout(() => {
        navigate(loginUrl);
      }, 3000);
      
    } catch (error) {
      logger.error('Password reset error', { error });
      setError(error.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <AuthPage>
        <AuthCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Container>
            <Header>
              <Title>Password Reset Successful</Title>
              <Subtitle>Your password has been updated</Subtitle>
            </Header>
            
            <SuccessMessage>
              <FiCheckCircle size={20} />
              <div>
                <strong>Password reset successful!</strong><br />
                You will be redirected to the login page shortly.
              </div>
            </SuccessMessage>
            
            <Button onClick={() => {
              // Get preserved redirect from localStorage
              const preservedRedirect = localStorage.getItem('faithinsight_login_redirect');
              const loginUrl = preservedRedirect 
                ? `/login?redirect=${encodeURIComponent(preservedRedirect)}`
                : '/login';
              navigate(loginUrl);
            }}>
              <FiArrowLeft size={16} />
              Go to Login
            </Button>
          </Container>
        </AuthCard>
      </AuthPage>
    );
  }

  return (
    <AuthPage>
      <AuthCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Container>
          <BackLink onClick={() => {
            // Get preserved redirect from localStorage
            const preservedRedirect = localStorage.getItem('faithinsight_login_redirect');
            const loginUrl = preservedRedirect 
              ? `/login?redirect=${encodeURIComponent(preservedRedirect)}`
              : '/login';
            navigate(loginUrl);
          }}>
            <FiArrowLeft size={16} />
            Back to Login
          </BackLink>

          <Header>
            <Title>Reset Password</Title>
            <Subtitle>Enter your new password below</Subtitle>
          </Header>

          <PasswordRequirements>
            <div style={{ fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.75rem', color: 'var(--text-primary)' }}>
              Password Requirements:
            </div>
            <Requirement className="info">
              <RequirementIcon valid={false}>•</RequirementIcon>
              At least 10 characters
            </Requirement>
            <Requirement className="info">
              <RequirementIcon valid={false}>•</RequirementIcon>
              At least one digit (0-9)
            </Requirement>
            <Requirement className="info">
              <RequirementIcon valid={false}>•</RequirementIcon>
              At least one special character (!@#$%^&*)
            </Requirement>
          </PasswordRequirements>

          <Form onSubmit={handleSubmit} style={{ marginTop: '1.5rem' }}>
            <FormGroup>
              <InputWrapper>
                <IconWrapper>
                  <FiLock size={16} />
                </IconWrapper>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="Enter new password"
                  disabled={loading}
                  autoFocus
                />
                <ToggleButton
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </ToggleButton>
              </InputWrapper>
              {formData.password && (
                <PasswordRequirements>
                  <Requirement className={passwordRequirements.length ? 'valid' : 'invalid'}>
                    <RequirementIcon valid={passwordRequirements.length}>✓</RequirementIcon>
                    At least 10 characters
                  </Requirement>
                  <Requirement className={passwordRequirements.digit ? 'valid' : 'invalid'}>
                    <RequirementIcon valid={passwordRequirements.digit}>✓</RequirementIcon>
                    At least one digit
                  </Requirement>
                  <Requirement className={passwordRequirements.specialChar ? 'valid' : 'invalid'}>
                    <RequirementIcon valid={passwordRequirements.specialChar}>✓</RequirementIcon>
                    At least one special character
                  </Requirement>
                </PasswordRequirements>
              )}
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <IconWrapper>
                  <FiLock size={16} />
                </IconWrapper>
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  placeholder="Confirm new password"
                  disabled={loading}
                />
                <ToggleButton
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={loading}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </ToggleButton>
              </InputWrapper>
            </FormGroup>

            {error && (
              <ErrorMessage>
                <FiAlertCircle size={14} />
                {error}
              </ErrorMessage>
            )}

            <Button type="submit" disabled={loading || !token || !isPasswordValid || formData.password !== formData.confirmPassword}>
              {loading ? (
                <>
                  <LoadingSpinner />
                  Resetting Password...
                </>
              ) : (
                <>
                  <FiCheckCircle size={16} />
                  Reset Password
                </>
              )}
            </Button>
          </Form>
        </Container>
      </AuthCard>
    </AuthPage>
  );
}

export default ResetPassword;
